import { HttpClient } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, take } from "rxjs/operators";
import { RaciNavigationService } from "../../../navigation/raci-navigation.service";
import { ValuationEventTaskDto } from "../models/valuation-event-task-dto";
import { AppServiceHelperService } from "../../../services/app-service-helper.service";
import { EventBusService } from "../../../services/event-bus.service";

/**
 * Raci service
 */
@Injectable({
  providedIn: 'root'
})

export class RaciService {
  /**
   * Show project steps event
   */
  _showProjectStepsEvent: EventEmitter<any> = new EventEmitter<any>();

  /**
   * The resource for Reflist.
   */
  private _reflistResource: string;

  /**
   * Constructor
   * @param httpClient HttpClient
   */
  constructor(private eventBus: EventBusService,
    private httpClient: HttpClient,
    private raciNavigationService: RaciNavigationService,
    private appServiceHelper: AppServiceHelperService) {
    this._reflistResource = this.appServiceHelper.getServiceEndpoint('pollux.api', 'reflist/pollux');
  }

  /**
   * Gets steps for portfolio
   * @param portfolioId The portfolio for which steps are being returned
   * @param projectId Project id
   */
  getPortfolioSteps(portfolioId: number, projectId: number): Observable<any[]> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/portfolio/steps?portfolioId=${portfolioId}&projectId=${projectId}`)).pipe(take(1));
  }

  /**
  * Gets steps for project
  * @param portfolioId The project for which steps are being returned
  */
  getProjectSteps(projectId: number): Observable<any[]> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/project/steps?projectId=${projectId}`)).pipe(take(1));
  }

  /**
  * Gets portfolio
  * @param portfolioId The portfolio for which steps are being returned
  */
  getValuationEvent(portfolioId: number): Observable<any[]> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/portfolio?portfolioId=${portfolioId}`)).pipe(take(1));
  }

  /**
  * Updates a checklist
  * @param checklistItemId Checklist that will be updated
  * @param checklistItemStatus Status of checklist item
  */
  updateChecklistItem(checklistItemId: number, checklistItemStatus: boolean): Observable<any> {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/checklistItem/update?checklistItemId=${checklistItemId}&isCompleted=${checklistItemStatus}`), null).pipe(
      map((data) => {
        if (!data.success) {
          alert("Failed to update checklist");
        }
        return data.success;
      })
    );
  }

  /**
   * Retrieves project participants
   * @param getExternalUsers Get external users
   * @param portfolioId Portfolio Id
   * @param projectId Project Id
   * */
  public getParticipants(getExternalUsers: boolean, portfolioId: any, projectId: any): Observable<any> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/project/participants?projectId=${projectId}&getExternalUsers=${getExternalUsers}&portfolioId=${portfolioId}`)).pipe(take(1));
  }

  /**
   * Retrieves valuation events
   * @param projectId ProjectId
   */
  getValuationEvents(projectId: number): Observable<any[]> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/valuationEvents?projectId=${projectId}`)).pipe(take(1));
  }

  /**
   * Retrieves portfolio properties
   * @param portfolioId portfolioId
   */
  getPortfolioProperties(portfolioId: number): Observable<any[]> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/portfolio/properties?portfolioId=${portfolioId}`)).pipe(take(1));
  }

  /**
  * Retrive all users
  * @param onlyActive Get only active users
  * @param getExternalUsers Get only active users
  */
  getUsers(onlyActive = false, getExternalUsers = false): Observable<any[]> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.settings.api', `user/getAll?onlyActive=${onlyActive}&getExternalUsers=${getExternalUsers}`)).pipe(take(1));
  }

  /**
   * Retrive reports of project and portfolio
   * @param projectId Project Id
   * @param portfolioId Portfolio Id
   * */
  getReports(projectId: number, portfolioId: number): Observable<any[]> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/reports?projectId=${projectId}&portfolioId=${portfolioId}`)).pipe(take(1));
  }

  /**
 * Retrive data for ve basic task
 * @param oid VE oid

 * */
  getVEBasicTaskDetails(oid: number): Observable<ValuationEventTaskDto> {
    return this.httpClient.get<ValuationEventTaskDto>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `general/tasks/tasks/veBasic?oid=${oid}`));
  }

  /**
* Save data for ve basic task
* @param task ValuationEventTaskDto
* */
  saveVEBasicTaskDetails(task: ValuationEventTaskDto): Observable<boolean> {
    return this.httpClient.post<boolean>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `general/tasks/tasks/veBasic`), task);
  }

  /**
 * Retrieves portfolios with properties
 * @param projectId Project Id
 * */
  getProjectValuationEventsProperties(projectId: number): Observable<any> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `ValuationPortfolios/project/portfolios/properties?projectId=${projectId}`)).pipe(take(1));
  }

  /**
   * Retrieves management roles
   * */
  getProjectRoles() {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/project/roles`)).pipe(take(1));
  }

  /**
   * Retrieves approval history
   * @param taskId Task ID
   * */
  getApprovalHistory(taskId: any): Observable<any> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/approvalHistory?taskId=${taskId}`)).pipe(take(1));
  }

  /**
   * Retrieves valorem history
   * @param portfolioId Portfolio Id
   * */
  getValoremHistory(portfolioId: any): Observable<any> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/valoremHistory?portfolioId=${portfolioId}`)).pipe(take(1));
  }

  /**
   * Update project participants
   * @param participants List of participants that will be saved
   */
  updateProjectParticipants(participants: any): Observable<any> {
    if (participants?.length > 0) {
      return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/assign/participants`), participants).pipe(
        map((data) => {
          if (!data.success) {
            alert("Failed to update project participants");
          }
          else {
            this.eventBus.broadcastEvent("openConfirmDialog", {
              success: () => {
                //Nothing
              }
            }, 'savedParticipantsSuccessfully');
          }
          return data.success;
        })
      );
    }
  }

  /**
   * Updates approval matrix
   * @param approval Approval matrix that needs to be updated/created
   */
  updateApprovalMatrix(approval: any): Observable<any> {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/updateApprovalMatrix`), approval).pipe(
      map((data) => {
        if (!data.success) {
          alert("Failed to update approval matrix");
        }
        else {
          this.eventBus.broadcastEvent("openConfirmDialog", {
            success: () => {
              //Nothing
            }
          }, 'saveApprovalSuccessfully');
          this.raciNavigationService.onStepsResetShouldConfirmDialog(false);
          this.raciNavigationService.onHostResetShouldConfirmDialog(false);
        }

        return data.success;
      })
    );
  }

  /**
   * Emit show project steps event
   * */
  showProjectStepsEvent() {
    this._showProjectStepsEvent.emit();
  }

  /**
 * Loads a reflist
 * @param name The reflist name (ref_xyz)
 * @param locale The locale in en-US format
 */
  loadReflist(name: string, locale: string = null) {
    let url = `${this._reflistResource}?reflistName=${name}&withParentNo=false`;
    if (locale) {
      const localeOverride = locale.replace('-', '_');
      url += `&localeOverride=${localeOverride}`;
    }

    return this.httpClient.get(url);
  }

  /**
    * Complete specified step
    * @param step Step data
    */
  completeStep(step: any) {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/complete/step`), step).pipe(
      map((data) => {
        if (!data.success) {
          alert("Failed to complete step");
        }
        return data.success;
      })
    );
  }

  /**
  * Complete specified task
  * @param task Task data
  */
  completeTask(task: any) {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/complete/task`), task).pipe(
      map((data) => {
        if (!data.success) {
          alert("Failed to complete task");
        }
        return data.success;
      })
    );
  }

  /**
   * Approve approval task
   * @param approval Approval data
   * @param approvedOid Approver Oid
   */
  approve(approvals: any, comment: string, approverOid: number) {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/approve?comment=${comment}&approverOid=${approverOid}`), approvals).pipe(
      map((data) => {
        if (!data.success) {
          alert("Failed to approve");
        }
        return data.success;
      })
    );
  }

  /**
   * Reject approvals
   * @param approvals Approovals
   * @param comment Comment message
   * @param rejecterOid Approver Oid
   */
  rejectApprovals(approvals: any, comment: any, rejecterOid: number) {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/reject?comment=${comment}&rejecterOid=${rejecterOid}`), approvals).pipe(
      map((data) => {
        if (!data.success) {
          alert("Failed to reject approve");
        }
        return data.success;
      })
    );
  }

  /**
   * Update task action stage
   * @param taskId Task oid
   * @param stepId Step oid
   */
  updateTaskActionStage(taskId: number, stepId: number) {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/update/task/stage?taskId=${taskId}&stepId=${stepId}`), null).pipe(
      map((data) => {
        if (!data.success) {
          alert("Failed to update action stage");
        }
        return data.success;
      })
    );
  }

  /**
 * Update task action stage
 * @param stepId Step oid
 */
  updateStepActionStage(stepId: number) {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/update/step/stage?stepId=${stepId}`), null).pipe(
      map((data) => {
        if (!data.success) {
          alert("Failed to update action stage");
        }
        return data.success;
      })
    );
  }

  /**
   * Updates active task
   */
  updateActiveTask(task: any): Observable<any> {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/updateTask`), task).pipe(
      map((data) => {
        if (!data.success) {
          alert("Failed to update action stage");
        }
        return data.success;
      })
    );
  }

  /**
   * Save external user
   * @param externalUser External user
   * */
  saveExternalUser(externalUser: any): Observable<any> {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.settings.api', `user`), externalUser).pipe(
      map((data) => {
        if (!data.m_Item1) {
          alert("Failed to save external user");
        }
        return data.m_Item2;
      })
    );
  }

  /**
   * Show or hide data loader
   * @param state State
   * */
  displayDataLoader(state: boolean) {
    this.eventBus.broadcastEvent("dataLoader", {
      displayLoader: () => {
        return state;
      }
    });
  }

  /**
   * Create reports for portfolio
   * @param portfolioId Portfolio Id
   * */
  createReports(portfolioId: any) {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `Reports/bulkCreate?valuationProjectId=${portfolioId}`), portfolioId).pipe(
      map((data) => {
        if (data) {
          return data;
        }
      })
    );
  }

  /**
   * Finalize portfolio
   * @param portfolioId Portfolio Id
   * */
  finalizePortfolio(portfolioId: any) {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `ValuationPortfolios/finalize-portfolio?oid=${portfolioId}`), portfolioId).pipe(
      map((data) => {
        if (data) {
          return data;
        }
      })
    );
  }

  /**
   * Close project and finalize portfolios
   * @param projectId Project Id
   * */
  closeProject(projectId: any) {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `ValuationPortfolios/close/project?projectId=${projectId}`), projectId).pipe(
      map((data) => {
        if (data) {
          return data;
        }
      })
    );
  }

  /**
   * Checks if project is closed
   * @param projectId Project Id
   * */
  isProjectClosed(projectId: any): Observable<any> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `ValuationPortfolios/project/isClosed?projectId=${projectId}`)).pipe(take(1));
  }

  /**
   * Sends a request towards the Capforce in order to close the opportunity
   * @param opportunityId Opportunity Id
   * @param projectId Project Id
   * */
  closeOpportunity(opportunityId: string, projectId: any): Observable<any> {
    const resourceUrl = this.appServiceHelper.getServiceEndpoint('pollux.pixwintegration.capforce.api', 'capforceIntegration');
    return this.httpClient.post<any>(`${resourceUrl}/send/close-opportunity?opportunityId=${opportunityId}&projectId=${projectId}`, null);
  }

  /**
   * Update project portfolios properties
   * @param properties Properties
   */
  saveProjectPortfoliosProperties(properties: any) {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/project/portfolios/properties`), properties).pipe(
      map((data) => {
        if (!data.success) {
          alert("Failed to update properties");
        }
        return data.success;
      })
    );
  }

  /**
   * Close valuation event and create new one
   * @param portfolio Portfolio
   * */
  createValuationEvent(portfolio: any, copyProperties: any, copyFxTable: any) {
    return this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `ValuationPortfolios/create/valuationEvent?copyProperties=${copyProperties}&copyFxTable=${copyFxTable}`), portfolio).pipe(
      map((data) => {
        if (data) {
          return data;
        }
      })
    );
  }

  /**
   *
   * Get cf approval project regions
   */
  cfApprovalProjectRegions() {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `approval/cfApprovalProjectRegions`)).pipe(take(1));
  }

  /**
   * Load business units
   * */
  loadBusinessUnits(): Observable<any> {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.settings.api', `user/businessUnits`)).pipe(take(1));
  }
}
